import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Code2, ExternalLink, Eye } from "lucide-react"

type HtmlDocumentViewerProps = {
  html: string
  documentTitle?: string
}

/**
 * Sandboxed preview for stored HTML documents (canvas mockups, static pages).
 * Scripts run only inside the iframe (sandbox + srcDoc).
 */
export function HtmlDocumentViewer({ html, documentTitle }: HtmlDocumentViewerProps) {
  const [mode, setMode] = useState<"preview" | "source">("preview")

  const openInNewTab = () => {
    const blob = new Blob([html], { type: "text/html;charset=utf-8" })
    const url = URL.createObjectURL(blob)
    window.open(url, "_blank", "noopener,noreferrer")
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center gap-2 border-b border-border bg-muted/30 px-3 py-2">
        <Button
          type="button"
          variant={mode === "preview" ? "secondary" : "ghost"}
          size="sm"
          onClick={() => setMode("preview")}
        >
          <Eye className="mr-1 h-4 w-4" aria-hidden />
          Preview
        </Button>
        <Button
          type="button"
          variant={mode === "source" ? "secondary" : "ghost"}
          size="sm"
          onClick={() => setMode("source")}
        >
          <Code2 className="mr-1 h-4 w-4" aria-hidden />
          Source
        </Button>
        <Button type="button" variant="outline" size="sm" className="ml-auto" onClick={openInNewTab}>
          <ExternalLink className="mr-1 h-4 w-4" aria-hidden />
          Open in browser
        </Button>
      </div>
      <div className="min-h-0 flex-1 print-visible" data-html-document-preview>
        {mode === "preview" ? (
          <iframe
            title={documentTitle || "HTML preview"}
            sandbox="allow-scripts allow-same-origin allow-forms"
            srcDoc={html}
            className="h-full w-full border-0 bg-background"
          />
        ) : (
          <pre className="h-full overflow-auto break-all whitespace-pre-wrap bg-muted/20 p-4 font-mono text-xs text-foreground">
            {html}
          </pre>
        )}
      </div>
    </div>
  )
}
